import { z } from 'zod';
import { recipientSchema } from './shipping.js';
/**
 * Bulk CSV runs.
 *
 * The Base44 build drove these from the browser in batches of 25: close the tab
 * mid-run and the progress record was gone while the money was already spent.
 * A run now lives in the database and is advanced by a server-side worker; the
 * page is only a viewer, and can be closed and reopened freely.
 */
export const batchStatusSchema = z.enum([
    'PENDING',
    'VALIDATING',
    'NEEDS_REVIEW',
    'READY',
    'PURCHASING',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
]);
export const batchItemStatusSchema = z.enum([
    'PENDING',
    'NEEDS_REVIEW',
    'READY',
    'PURCHASING',
    'SUCCEEDED',
    'FAILED',
    'SKIPPED',
]);
export const rateStrategySchema = z.enum(['FIXED_SERVICE', 'CHEAPEST_PER_PARCEL']);
/**
 * Upper bound on one run.
 *
 * Not a technical limit — the worker streams through items — but a guard
 * against a mis-mapped spreadsheet turning into thousands of real purchases.
 * A genuinely larger run can be split, and splitting is cheap; an accidental
 * one is not.
 */
export const MAX_BATCH_ROWS = 2000;
export const createBatchSchema = z.object({
    orderName: z.string().trim().min(1, 'Give the order a name').max(120),
    orderNumber: z.string().trim().min(1, 'Give the order a number').max(60),
    shippingBoxId: z.string().optional(),
    box: z.object({
        length: z.coerce.number().positive().max(999),
        width: z.coerce.number().positive().max(999),
        height: z.coerce.number().positive().max(999),
    }),
    weight: z.coerce.number().positive('Weight must be greater than 0').max(1000),
    rows: z
        .array(recipientSchema)
        .min(1, 'The file has no rows to ship')
        .max(MAX_BATCH_ROWS, `A single run is limited to ${MAX_BATCH_ROWS} rows`),
});
/** How an operator resolves a row the address check flagged. */
export const resolveBatchItemSchema = z.object({
    action: z.enum([
        /** Replace the address, with the corrected values in `recipient`. */
        'correct',
        /** Ship it exactly as typed — the operator knows better than the validator. */
        'override',
        /** Do not ship this row at all. */
        'skip',
    ]),
    recipient: recipientSchema.optional(),
});
export const startBatchSchema = z.object({
    rateStrategy: rateStrategySchema.default('FIXED_SERVICE'),
    /**
     * Which service to buy for every parcel, for FIXED_SERVICE.
     *
     * Provider and service rather than a rate id, because rate ids are issued per
     * shipment: the worker prices each row separately and has to match the chosen
     * service on each one.
     */
    rateProvider: z.string().trim().min(1).optional(),
    rateService: z.string().trim().min(1).optional(),
    /** Ceiling per parcel, for CHEAPEST_PER_PARCEL. Stops a surprise on an outlier. */
    maxAmountPerParcel: z.coerce.number().positive().optional(),
});
/** True once the run has stopped moving, whether or not it went well. */
export function isTerminalStatus(status) {
    return status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';
}
// ---------------------------------------------------------------------------
// CSV column mapping
// ---------------------------------------------------------------------------
export const COLUMN_FIELDS = [
    'fullName',
    'firstName',
    'lastName',
    'address1',
    'address2',
    'city',
    'state',
    'zip',
    'country',
];
/**
 * Patterns for guessing which CSV column is which.
 *
 * Carried over from the original app and kept in the same order, because the
 * order is load-bearing: 'address' has to be tried before 'address2', or a
 * sheet with both columns maps the wrong one to street address.
 */
const PATTERNS = {
    fullName: ['full name', 'fullname', 'name', 'recipient name', 'recipient', 'customer name', 'contact name'],
    firstName: ['first name', 'firstname', 'first', 'fname'],
    lastName: ['last name', 'lastname', 'last', 'lname', 'surname'],
    address1: ['address', 'address1', 'address 1', 'street', 'street address', 'street1', 'address line 1', 'addr1', 'line1'],
    address2: ['address2', 'address 2', 'street2', 'address line 2', 'addr2', 'line2', 'suite', 'apt', 'apartment', 'unit'],
    city: ['city', 'town', 'municipality'],
    state: ['state', 'province', 'region', 'st'],
    zip: ['zip', 'zipcode', 'zip code', 'postal', 'postal code', 'postcode'],
    country: ['country', 'country code', 'nation'],
};
/**
 * Guess a column mapping from CSV headers.
 *
 * Each column can only be claimed once — without that, 'address' matches both
 * address1 and address2 and the same data lands in two fields.
 */
export function autoDetectMapping(headers) {
    const normalized = headers.map((header) => header.toLowerCase().trim());
    const claimed = new Set();
    const mapping = {};
    const claim = (field) => {
        // Exact matches first across all patterns, so a column literally called
        // "state" is not stolen by a fuzzy match on "estate".
        for (const pattern of PATTERNS[field]) {
            const index = normalized.indexOf(pattern);
            if (index !== -1 && !claimed.has(headers[index])) {
                claimed.add(headers[index]);
                return headers[index];
            }
        }
        for (const pattern of PATTERNS[field]) {
            const index = normalized.findIndex((header, i) => !claimed.has(headers[i]) && (header.includes(pattern) || pattern.includes(header)));
            if (index !== -1) {
                claimed.add(headers[index]);
                return headers[index];
            }
        }
        return undefined;
    };
    // A single full-name column wins over first/last; only look for the split
    // form when there is no combined one.
    const fullName = claim('fullName');
    if (fullName) {
        mapping.fullName = fullName;
    }
    else {
        mapping.firstName = claim('firstName');
        mapping.lastName = claim('lastName');
    }
    mapping.address1 = claim('address1');
    mapping.address2 = claim('address2');
    mapping.city = claim('city');
    mapping.state = claim('state');
    mapping.zip = claim('zip');
    mapping.country = claim('country');
    return mapping;
}
/** Whether a mapping has everything a label needs. */
export function isMappingComplete(mapping) {
    const hasName = Boolean(mapping.fullName || mapping.firstName || mapping.lastName);
    return hasName && Boolean(mapping.address1 && mapping.city && mapping.state && mapping.zip);
}
//# sourceMappingURL=batch.js.map